import { type ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import type { SettingsResponse } from "../../shared/types";
import { useAuth } from "../auth/AuthContext";
import { BrandGlyph, BrandWord } from "../components/BrandMark";
import { SeasonCard } from "../components/season/Card";
import { DockedBar } from "../components/season/DockedBar";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import type { SeasonLayout } from "../components/season/layout";
import { Page } from "../components/season/Page";
import { GroupHead } from "../components/season/PageHead";
import { Pane, Panes } from "../components/season/Panes";
import { Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Icon, type IconName } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { useToast } from "../components/ui/Toast";
import { OpensRows, OwnAddressRows, SlugStatus } from "../settings/SeasonPanes";
import { formatAt } from "../lib/zones";
import { useAddressNext, useSlugCheck } from "./AddressStep";
import { useExampleDomain, useSendingAddress } from "./EmailSteps";
import { HOSTS } from "./hosts";
import { type LookMode, usePreviewWho } from "./parts";
import { BrowserFrame, EmailPreview, guestInbox, hostInbox, InboxMock, PreviewFit, SignupPreview, usePreviewRoom } from "./Previews";
import { type ReadyAct, readySummary } from "./ready";
import { recordName, rootOf, TRACK_NAMES, type TrackKind, trackProblem } from "./rules";
import { BusinessRow, Callout, HostSteps, LookTiles, PasteRows, PasteSheets, PreviewSwitch, RecMark, SeasonRecCard, StField, StJoin, sampleRecord, usePaste } from "./SeasonParts";
import { READY_ICON, useSetupSeen } from "./SetupView";
import { SETUP_STEPS, setupCount } from "./SetupContext";
import { EVENTS } from "./TrackingStep";
import { useLookChoice } from "./useLookChoice";
import { useRecordCheck } from "./useRecordCheck";
import { useSenderForm } from "./useSenderForm";
import type { Run } from "./useSettingsData";
import { useTrackWork } from "./useTracking";
import "../styles/season-settings.css";

interface Props {
  layout: SeasonLayout;
  data: SettingsResponse | null;
  failed: boolean;
  run: Run;
  setData: (d: SettingsResponse) => void;
  skip: () => Promise<void>;
}

interface StepProps {
  layout: SeasonLayout;
  data: SettingsResponse;
  run: Run;
  setData: (d: SettingsResponse) => void;
  skip: () => Promise<void>;
}

type StepKey = (typeof SETUP_STEPS)[number]["key"];
const STEP_ICON: IconName[] = ["palette", "mail", "globe", "target"];

interface Frame {
  layout: SeasonLayout;
  data: SettingsResponse;
  skip: () => Promise<void>;
  /** The step this screen is, or null for the welcome and the ready screen. */
  step?: StepKey;
  title: ReactNode;
  children: ReactNode;
  /** The detail pane on the iPad on its side. */
  side?: ReactNode;
  /** Where Back goes; none on the welcome and the ready screen. */
  back?: string;
  /** The main action, and what sits beside it. */
  next: { label: ReactNode; onClick: () => void; disabled?: boolean; icon?: IconName; after?: IconName };
  /** A quiet button before the main one (Skip this, Go to Home). */
  extra?: { label: string; onClick: () => void; disabled?: boolean };
  /** Phone only: the words docked beside the main action. */
  status?: ReactNode;
  /** Whether this is the ready screen, which has no Skip for now. */
  ready?: boolean;
}

const brand = (
  <span className="st-brand">
    <BrandGlyph size={28} />
    <BrandWord />
  </span>
);

function Progress({ ix, data }: { ix: number; data: SettingsResponse }) {
  return (
    <div className="st-prog" role="img" aria-label={`Step ${ix + 1} of 4`}>
      {SETUP_STEPS.map((s, i) => (
        <i key={s.key} className={cx((i < ix || data.setup[s.key]) && "done", i === ix && "on")} />
      ))}
    </div>
  );
}

function Kicker({ ix }: { ix: number }) {
  return (
    <p className="st-kick">
      Step {ix + 1} of 4 · {SETUP_STEPS[ix].label}
      {ix === 3 ? " · Optional" : ""}
    </p>
  );
}

function StepList({ cur, data, skip }: { cur?: StepKey; data: SettingsResponse; skip: () => Promise<void> }) {
  const navigate = useNavigate();
  return (
    <>
      <GroupedList>
        {SETUP_STEPS.map((s, i) => {
          const done = data.setup[s.key];
          return (
            <ListRow
              key={s.key}
              lead={<span className={cx("k-ico", done ? "good" : cur === s.key && "wine")}>{done ? <Icon name="check" /> : <b>{i + 1}</b>}</span>}
              title={s.label}
              sub={i === 3 ? "Optional" : undefined}
              on={cur === s.key}
              noChevron
              onClick={() => navigate(s.path)}
            />
          );
        })}
      </GroupedList>
      <div className="st-gap">
        <Button variant="q" onClick={() => void skip()}>
          Skip for now
        </Button>
      </div>
    </>
  );
}

/** One setup screen, drawn for the layout: a phone's nav, progress and docked action; an iPad's centred column; or panes. */
function SetupFrame(f: Frame) {
  const navigate = useNavigate();
  const ix = f.step ? SETUP_STEPS.findIndex((s) => s.key === f.step) : -1;
  const head =
    ix >= 0 ? (
      <>
        <Progress ix={ix} data={f.data} />
        <Kicker ix={ix} />
      </>
    ) : null;
  const skipLink = f.ready ? null : (
    <button type="button" className="k-link st-skip" onClick={() => void f.skip()}>
      Skip for now
    </button>
  );
  const nextBtn = (full: boolean) => (
    <Button variant="p" size={full ? "md" : "lg"} full={full} icon={f.next.icon} after={f.next.after} disabled={f.next.disabled} onClick={f.next.onClick}>
      {f.next.label}
    </Button>
  );
  const extraBtn = f.extra ? (
    <Button variant="q" size="lg" disabled={f.extra.disabled} onClick={f.extra.onClick}>
      {f.extra.label}
    </Button>
  ) : null;

  if (f.layout === "phone") {
    return (
      <Page layout="phone" className="st-su">
        <div className="k-nav">
          {f.back ? (
            <button type="button" className="k-back" onClick={() => navigate(f.back ?? "/setup")}>
              <Icon name="left" />
              <span>Back</span>
            </button>
          ) : (
            brand
          )}
          <span className="k-nav-t" />
          <span className="k-nav-r">{skipLink}</span>
        </div>
        <div className="st-sbody">
          {head}
          {f.ready ? null : <h1 className="k-h1">{f.title}</h1>}
          {f.children}
        </div>
        <DockedBar>
          {f.status ? <span className="k-bar-t st-sbar-t">{f.status}</span> : null}
          {f.extra && !f.status ? (
            <Button variant="q" disabled={f.extra.disabled} onClick={f.extra.onClick}>
              {f.extra.label}
            </Button>
          ) : null}
          {nextBtn(!f.status)}
        </DockedBar>
      </Page>
    );
  }

  const foot = (
    <div className="st-foot">
      {f.back ? (
        <Button size="lg" icon="left" onClick={() => navigate(f.back ?? "/setup")}>
          Back
        </Button>
      ) : null}
      <span className="st-sp" />
      {f.status ? <span className="st-stat">{f.status}</span> : null}
      {extraBtn}
      {nextBtn(false)}
    </div>
  );
  const col = (
    <div className="st-col">
      {head}
      {f.ready ? null : <h1 className="k-h1">{f.title}</h1>}
      {f.children}
      {foot}
    </div>
  );
  if (f.layout === "upright" || !f.step) {
    return (
      <Page layout={f.layout} className="st-su">
        <div className="st-top">
          {brand}
          {skipLink}
        </div>
        {col}
      </Page>
    );
  }
  return (
    <div className="st-su-split">
      <Panes three={!!f.side} className="st-split">
        <Pane kind="list" label="Setup steps">
          <Page layout="side" className="st-su">
            <StepList cur={f.step} data={f.data} skip={f.skip} />
          </Page>
        </Pane>
        <Pane kind="main" label={SETUP_STEPS[ix].label} showing={f.step}>
          <Page layout="side" className="st-su">
            {col}
          </Page>
        </Pane>
        {f.side ? (
          <Pane kind="detail" showing={f.step}>
            <Page layout="side" className="st-su">
              {f.side}
            </Page>
          </Pane>
        ) : null}
      </Panes>
    </div>
  );
}

function Qs({ children }: { children: ReactNode }) {
  return <div className="st-qs">{children}</div>;
}

// --- Welcome ---

function Welcome({ layout, data, skip }: StepProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <SetupFrame
      layout={layout}
      data={data}
      skip={skip}
      title={<span className="st-wh">Hi {user?.first_name || "there"}. Let’s get Flashroom ready for your people.</span>}
      next={{ label: "Let’s start", after: "right", onClick: () => navigate("/setup/look") }}
    >
      <GroupedList className="st-wl">
        {SETUP_STEPS.map((s, i) => (
          <ListRow key={s.key} icon={STEP_ICON[i]} tone={i ? undefined : "wine"} title={s.label} sub={i === 3 ? "Optional" : undefined} />
        ))}
      </GroupedList>
    </SetupFrame>
  );
}

// --- 1. Your look ---

function LookStep(p: StepProps) {
  const navigate = useNavigate();
  const room = usePreviewRoom();
  const who = usePreviewWho(p.data, room);
  const look = useLookChoice(p.data, p.run);
  const preview = (
    <SeasonCard className="st-pvcard">
      <PreviewSwitch colours={look.colours} who={who} />
    </SeasonCard>
  );
  return (
    <SetupFrame
      {...p}
      step="look"
      back="/setup"
      title="How should your pages look?"
      side={
        <>
          <GroupHead title="Preview" />
          {preview}
        </>
      }
      next={{
        label: "Looks good, continue",
        after: "right",
        disabled: look.saving,
        onClick: () => void look.save().then((ok) => ok && navigate("/setup/email")),
      }}
    >
      <Qs>
        <BusinessRow business={who.business} logoUrl={p.data.brand.logo_url} />
        <LookTiles mode={look.mode as LookMode} colours={look.colours} onPick={look.pick} />
        {p.layout === "side" ? null : preview}
      </Qs>
    </SetupFrame>
  );
}

// --- 2a. Where emails come from ---

function EmailStep(p: StepProps) {
  const a = useSendingAddress(p.data, p.run);
  const example = useExampleDomain();
  const room = usePreviewRoom();
  const who = usePreviewWho(p.data, room);
  return (
    <SetupFrame
      {...p}
      step="email"
      back="/setup/look"
      title="Where should your emails come from?"
      side={
        <>
          <GroupHead title="A guest’s inbox" />
          <PreviewFit width={760}>
            <InboxMock rows={guestInbox(who, p.data.brand.accent)} />
          </PreviewFit>
        </>
      }
      next={{ label: "Continue", after: "right", disabled: !a.ok || a.busy, onClick: () => void a.next() }}
    >
      <Qs>
        <StField label="Your sending address" htmlFor="st-sub">
          <input dir="auto"
            id="st-sub"
            className="inp"
            value={a.sub}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="none"
            inputMode="url"
            onChange={(e) => a.setSub(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void a.next();
            }}
          />
        </StField>
        <div aria-live="polite">
          {a.note.kind === "hint" ? (
            <span className="st-hint">Like {example}</span>
          ) : a.note.kind === "bad" ? (
            <Callout tone="warn" icon="info">
              Use a web address, like <b>{example}</b>.
            </Callout>
          ) : a.note.kind === "bare" ? (
            <Callout tone="warn" icon="info">
              Use a part of <b>{a.note.domain}</b> just for sending.
              <Button onClick={() => a.setSub(a.note.kind === "bare" ? a.note.suggestion : "")}>Use {a.note.suggestion}</Button>
            </Callout>
          ) : (
            <Callout tone="good" icon="check">
              Looks good.
            </Callout>
          )}
        </div>
      </Qs>
    </SetupFrame>
  );
}

// --- 2b. The records ---

function RecordsStep(p: StepProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const s = p.data.sending;
  const verified = s.status === "verified";
  const rc = useRecordCheck(s.domain, verified, p.setData);
  const paste = usePaste(p.data);
  if (!s.domain) return <Navigate to="/setup/email" replace />;
  const root = rootOf(s.domain);
  const n = s.records.length;
  const found = s.records.filter((r) => r.found).length;
  const stateOf = (f: boolean) => (f ? "found" : rc.checking ? "checking" : "wait");
  const status = verified ? "Email verified" : rc.checking ? "Checking each record…" : `${found} of ${n} found`;
  const sample = sampleRecord(s.records, (x) => recordName(x, root));
  return (
    <SetupFrame
      {...p}
      step="email"
      back="/setup/email"
      title={`Add ${n} records to ${root}`}
      status={status}
      side={
        <>
          <GroupHead title="Where to paste them" action={<button type="button" className="k-link" onClick={() => paste.setHosts(true)}>Change</button>} />
          <SeasonCard>
            <h3 className="st-sh3 st-sh30">{HOSTS[paste.host].label}</h3>
            <HostSteps host={paste.host} root={root} sample={sample} />
          </SeasonCard>
        </>
      }
      next={
        verified
          ? { label: "Continue: send a test", onClick: () => navigate("/setup/email/test") }
          : { label: "Check now", icon: "refresh", disabled: rc.checking, onClick: () => void rc.check() }
      }
    >
      <Qs>
        <div className="st-recs">
          {s.records.map((r) => (
            <SeasonRecCard key={`${r.type} ${r.name}`} type={r.type} name={recordName(r.name, root)} value={r.value} state={verified ? "found" : stateOf(r.found)} />
          ))}
        </div>
        {verified ? (
          <Callout tone="good" icon="check">
            All {n} found. Your email is verified.
          </Callout>
        ) : rc.checks > 0 && !rc.checking ? (
          <Callout icon="clock">This can take a few minutes. We’ll email you when it’s done.</Callout>
        ) : null}
        <PasteRows hostLabel={HOSTS[paste.host].label} onHosts={() => paste.setHosts(true)} onSend={() => paste.setSteps(true)} sentTo={paste.sentTo} />
        {verified ? null : (
          <div className="st-later">
            <button
              type="button"
              className="k-link"
              onClick={() => {
                toast({ text: "Saved for later", icon: "clock" });
                navigate("/setup/address");
              }}
            >
              I’ll finish this later
            </button>
          </div>
        )}
      </Qs>
      <PasteSheets paste={paste} data={p.data} root={root} />
    </SetupFrame>
  );
}

// --- 2c. The test ---

function TestStep(p: StepProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const s = p.data.sending;
  const verified = s.status === "verified";
  const room = usePreviewRoom();
  const who = usePreviewWho(p.data, room);
  const form = useSenderForm(p.data, p.run, { fallbackName: who.business });
  const [sent, setSent] = useState<{ to: string; at: number } | null>(null);
  if (!s.domain) return <Navigate to="/setup/email" replace />;
  const at = sent ? formatAt(sent.at, { hour: "numeric", minute: "2-digit" }) : "";
  const testWho = { ...who, fromName: form.fromName || who.business, fromEmail: `${form.local}@${s.domain}` };
  const send = async () => {
    const to = await form.sendTest();
    if (!to) return;
    setSent({ to, at: Date.now() });
    toast({ text: "Test sent", icon: "inbox" });
  };
  return (
    <SetupFrame
      {...p}
      step="email"
      back="/setup/email/records"
      title="Send yourself a test"
      side={
        <>
          <GroupHead title={`Your inbox · ${form.reply}`} />
          {sent ? (
            <EmailPreview
              colours={p.data.brand}
              who={testWho}
              mail={{ subject: `Test from ${testWho.fromName}`, title: `This is a test from ${testWho.fromName}`, body: "If you’re reading this, your address works.", time: at, test: true, replyTo: form.reply }}
            />
          ) : (
            <PreviewFit width={760}>
              <InboxMock rows={hostInbox(testWho, p.data.brand.accent, false)} />
            </PreviewFit>
          )}
        </>
      }
      next={{ label: "Continue", after: "right", disabled: !verified, onClick: () => navigate("/setup/address") }}
    >
      <Qs>
        {verified ? null : (
          <Callout tone="warn" icon="info">
            Records not all found yet.{" "}
            <button type="button" className="k-link st-inl" onClick={() => navigate("/setup/email/records")}>
              Back to the records
            </button>
          </Callout>
        )}
        <div className="k-form">
          <StField label="From name" htmlFor="st-fn">
            <input dir="auto" id="st-fn" className="inp" value={form.fromName} onChange={(e) => form.setFromName(e.target.value)} onBlur={() => void form.saveName()} />
          </StField>
          <StField label="From address" htmlFor="st-fl">
            <StJoin id="st-fl" value={form.local} onChange={form.setLocal} onBlur={() => void form.saveLocal()} suffix={`@${s.domain}`} />
          </StField>
          <StField label="Replies go to" htmlFor="st-rt">
            <input id="st-rt" className="inp" type="email" inputMode="email" value={form.reply} onChange={(e) => form.setReply(e.target.value)} onBlur={() => void form.saveReply()} />
          </StField>
        </div>
        {sent ? (
          <Callout tone="good" icon="inbox">
            <b>It’s on its way to your inbox.</b> {sent.to} · {at}
          </Callout>
        ) : (
          <div>
            <Button icon="send" disabled={!verified || form.sending} onClick={() => void send()}>
              {form.sending ? "Sending…" : `Send a test to ${form.reply}`}
            </Button>
          </div>
        )}
      </Qs>
    </SetupFrame>
  );
}

// --- 3. Your web address ---

function AddressStep(p: StepProps) {
  const d = p.data.domain;
  const check = useSlugCheck(d.subdomain);
  const { busy, next } = useAddressNext(p.data, check.slug, check.avail);
  const room = usePreviewRoom(d.main_webinar_id);
  const who = usePreviewWho(p.data, room);
  const free = `${check.good}.flashsuite.net`;
  const shown = d.custom_domain_status === "verified" && d.custom_domain ? d.custom_domain : free;
  return (
    <SetupFrame
      {...p}
      step="address"
      back="/setup/email/test"
      title="Where will people find your rooms?"
      side={
        <>
          <GroupHead title="Your address opens" />
          <PreviewFit>
            <BrowserFrame url={shown}>
              <SignupPreview colours={p.data.brand} who={{ ...who, address: shown }} />
            </BrowserFrame>
          </PreviewFit>
        </>
      }
      next={{ label: "Continue", after: "right", disabled: check.avail !== "ok" || busy, onClick: () => void next() }}
    >
      <Qs>
        <StField label="Your free address" htmlFor="st-sslug">
          <StJoin id="st-sslug" value={check.slug} onChange={(v) => check.setSlug(v.toLowerCase())} suffix=".flashsuite.net" />
        </StField>
        <div aria-live="polite" className="st-slugst">
          <SlugStatus avail={check.avail} slug={check.slug} suggestion={check.suggestion} onUse={check.setSlug} />
        </div>
        <OwnAddressRows data={p.data} run={p.run} label="Use an address you own too" freeHost={free} />
        <GroupHead title="When someone opens the address, they see" />
        <OpensRows data={p.data} run={p.run} />
      </Qs>
    </SetupFrame>
  );
}

// --- 4. Tracking ---

const FIELDS: Record<TrackKind, { label: string; ph: string; hint: string }> = {
  meta: { label: "Pixel ID", ph: "15 or 16 digits", hint: "In Meta Events Manager, under Data sources." },
  ga: { label: "Measurement ID", ph: "G-XXXXXXXXXX", hint: "In Google Analytics, under Admin, then Data streams." },
  own: { label: "Paste the code your tool gave you", ph: "<script>…</script>", hint: "" },
};
const LETTER: Record<TrackKind, string> = { meta: "M", ga: "G", own: "" };

function TrackingStep(p: StepProps) {
  const opened = (useLocation().state as { open?: TrackKind } | null)?.open;
  const t = useTrackWork(p.data, p.run, p.setData, opened);
  const m = t.cards.meta.on;
  const g = t.cards.ga.on;
  return (
    <SetupFrame
      {...p}
      step="tracking"
      back="/setup/address"
      title="Do you run ads on Meta or Google?"
      side={
        <>
          <GroupHead title="What your tools hear" />
          <GroupedList noLead>
            {EVENTS.map(([icon, title, metaName, gaName]) => (
              <ListRow
                key={title}
                icon={icon}
                title={title}
                sub={
                  <span className="st-chips">
                    {m ? <span className="tag">{metaName}</span> : null}
                    {g && gaName ? <span className="tag">{gaName}</span> : null}
                    {!m && !(g && gaName) ? <span className="tag">Nothing yet</span> : null}
                  </span>
                }
              />
            ))}
          </GroupedList>
        </>
      }
      extra={{ label: "Skip this", disabled: t.busy, onClick: () => void t.skip() }}
      next={{ label: "Continue", after: "right", disabled: t.busy, onClick: () => void t.finish() }}
    >
      <Qs>
        {(["meta", "ga", "own"] as const).map((k) => {
          const c = t.cards[k];
          const f = FIELDS[k];
          const problem = trackProblem(k, c.value);
          return (
            <SeasonCard key={k} className={cx("st-tc", c.on && "on")} flush>
              <button type="button" className="st-tc-h" aria-pressed={c.on} onClick={() => t.patch(k, { on: !c.on, st: "none" })}>
                <span className="k-ico">{LETTER[k] ? <b>{LETTER[k]}</b> : <Icon name="code" />}</span>
                <b>{TRACK_NAMES[k]}</b>
                <span className="st-chkc">{c.on ? <Icon name="check" /> : null}</span>
              </button>
              {c.on ? (
                <div className="st-tb">
                  <StField label={f.label} htmlFor={`st-t-${k}`} hint={f.hint}>
                    {k === "own" ? (
                      <textarea dir="auto" id={`st-t-${k}`} className="inp" rows={3} placeholder={f.ph} spellCheck={false} value={c.value} onChange={(e) => t.patch(k, { value: e.target.value, st: "none" })} />
                    ) : (
                      <input dir="auto" id={`st-t-${k}`} className="inp" placeholder={f.ph} inputMode={k === "meta" ? "numeric" : "text"} spellCheck={false} autoComplete="off" value={c.value} onChange={(e) => t.patch(k, { value: e.target.value, st: "none" })} />
                    )}
                  </StField>
                  <p className="st-need">{problem}</p>
                  {k === "meta" ? (
                    <StField label={<>Conversions API token <span className="st-opt">(optional)</span></>} htmlFor="st-t-token">
                      <input id="st-t-token" className="inp" type="password" autoComplete="off" placeholder={t.tokenSaved ? "Saved. Paste a new one to replace it" : "Paste the token"} value={c.token} onChange={(e) => t.patch(k, { token: e.target.value })} />
                    </StField>
                  ) : null}
                  <div className="st-chk">
                    <Button icon="refresh" disabled={c.st === "checking"} onClick={() => void t.checkLoads(k)}>
                      Check it loads
                    </Button>
                    <span aria-live="polite">
                      {c.st === "loaded" ? <RecMark state="found" found="Loaded on your pages" /> : null}
                      {c.st === "checking" ? (
                        <span className="st-rst plain">
                          <span className="st-spin" />
                          Opening your sign-up page
                        </span>
                      ) : null}
                      {c.st === "waiting" ? (
                        <span className="st-rst plain">
                          <span className="st-o" />
                          Waiting for a visit
                        </span>
                      ) : null}
                      {c.st === "bad" ? <span className="st-bad">Needs a valid ID</span> : null}
                    </span>
                  </div>
                </div>
              ) : null}
            </SeasonCard>
          );
        })}
      </Qs>
    </SetupFrame>
  );
}

// --- Ready ---

function ReadyAction({ act }: { act: ReadyAct }) {
  const navigate = useNavigate();
  if (act.kind === "pill") {
    return (
      <Pill tone={act.tone} dot>
        {act.text}
      </Pill>
    );
  }
  if (act.kind === "copy") return <CopyAction value={act.value} />;
  return (
    <Button variant={act.primary ? "p" : "plain"} onClick={() => navigate(act.to)}>
      {act.label}
    </Button>
  );
}

function CopyAction({ value }: { value: string }) {
  const toast = useToast();
  return (
    <Button
      icon="copy"
      onClick={() =>
        void navigator.clipboard
          ?.writeText(value)
          .then(() => toast({ text: "Copied", icon: "copy" }))
          .catch(() => undefined)
      }
    >
      Copy
    </Button>
  );
}

function Ready(p: StepProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const n = setupCount(p.data.setup);
  const first = user?.first_name || "there";
  useSetupSeen(p.data.setup.seen);
  const rows = readySummary(p.data, user?.workspace_name ?? "");
  return (
    <SetupFrame
      {...p}
      ready
      title={n === 4 ? `You’re ready, ${first}.` : `Nearly there, ${first}.`}
      next={{ label: "Make your first room", icon: "plus", onClick: () => navigate("/rooms/new") }}
      extra={p.layout === "phone" ? undefined : { label: "Go to Home", onClick: () => navigate("/") }}
    >
      <div className="st-ready">
        <span className="st-bigck">
          <Icon name="check" />
        </span>
        <h1 className="k-h1">{n === 4 ? `You’re ready, ${first}.` : `Nearly there, ${first}.`}</h1>
        {n === 4 ? null : <p className="k-sub">{n} of 4 done</p>}
      </div>
      <GroupedList className="st-rl">
        {rows.map((r) => (
          <ListRow
            key={r.key}
            lead={
              <span className={cx("k-ico", r.done ? "good" : "brass")}>
                <Icon name={r.done ? "check" : READY_ICON[r.key]} />
              </span>
            }
            title={r.title}
            sub={r.sub}
            clamp
            end={<ReadyAction act={r.act} />}
          />
        ))}
      </GroupedList>
      {p.layout === "phone" ? (
        <div className="st-home">
          <Button variant="q" onClick={() => navigate("/")}>
            Go to Home
          </Button>
        </div>
      ) : null}
    </SetupFrame>
  );
}

// --- The setup ---

/** First-run setup for a phone and an iPad: a welcome, then one question per screen, and the ready screen. */
export function SeasonSetup({ layout, data, failed, run, setData, skip }: Props) {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const id = parts[1] ?? "";
  const sub = parts[2] ?? "";
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  if (!data) {
    return (
      <Page layout={layout} className="st-su">
        <p className="k-sub" aria-busy={!failed}>
          {failed ? "We couldn’t load your settings. Refresh to try again." : "Loading…"}
        </p>
      </Page>
    );
  }
  const p: StepProps = { layout, data, run, setData, skip };
  if (id === "") return <Welcome {...p} />;
  if (id === "look") return <LookStep {...p} />;
  if (id === "email" && sub === "") return <EmailStep {...p} />;
  if (id === "email" && sub === "records") return <RecordsStep {...p} />;
  if (id === "email" && sub === "test") return <TestStep {...p} />;
  if (id === "address") return <AddressStep {...p} />;
  if (id === "tracking") return <TrackingStep {...p} />;
  if (id === "ready") return <Ready {...p} />;
  return <Navigate to="/setup" replace />;
}
